import {
	createContext,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { toast } from "react-toastify";

import type {
	TQuickStart,
	TQuickStartResponse,
} from "../types/quickStart.type";

import { services } from "../services";
import { getApiErrorMessage } from "../utils/apiErrorUtils";

export type TQuickStartFlow = {
	record: TQuickStart;
	currentStep?: string;
	isRunning: boolean;
	busy: boolean;
	revision: number;
	acceptResponse: (
		response: TQuickStartResponse,
		navigateOff?: boolean
	) => Promise<void>;
	start: (skip?: boolean) => Promise<void>;
	reopen: (step: string) => Promise<void>;
};

export const QuickStartContext =
	createContext<TQuickStartFlow | null>(null);

/** One wizard-wide subscription and a GET watchdog independent of stream traffic. */
export const useQuickStart = ({
	kind,
	id,
	seed,
	enabled = true,
	refreshMeta,
	goToStep,
}: {
	kind: "lithe" | "dataset";
	id: string;
	seed?: TQuickStartResponse;
	enabled?: boolean;
	refreshMeta: () => Promise<unknown>;
	goToStep: (step: string) => void;
}): TQuickStartFlow => {
	const key = `${kind}:${id}`;
	const [state, setState] = useState<{
		key: string;
		record: TQuickStart;
		currentStep?: string;
	}>({ key, record: {} });
	const [busy, setBusy] = useState(false);
	const [revision, setRevision] = useState(0);
	const terminalRef = useRef("");
	const scopeRef = useRef(key);
	scopeRef.current = key;
	const callbacks = useRef({ refreshMeta, goToStep });
	callbacks.current = { refreshMeta, goToStep };
	const record =
		state.key === key
			? state.record
			: (seed?.quick_start ?? {});
	const isRunning = enabled && record.status === "running";
	const currentStep =
		state.key === key
			? state.currentStep
			: seed?.current_step;

	const acceptResponse = useCallback(
		async (
			response: TQuickStartResponse,
			navigateOff = false
		) => {
			if (scopeRef.current !== key) return;
			const next = response.quick_start ?? {};
			const nextState = {
				key,
				record: next,
				currentStep: response.current_step,
			};
			if (next.status === "running") {
				setState(nextState);
				terminalRef.current = "";
				return;
			}
			const terminal =
				next.status === "done" || next.status === "stopped";
			const target =
				next.status === "stopped"
					? (next.stopped_at ?? response.current_step)
					: response.current_step;
			const signature = `${key}:${next.status}:${target}:${next.reason}`;
			if (
				(!terminal || terminalRef.current === signature) &&
				!navigateOff
			) {
				if (!terminal) setState(nextState);
				return;
			}
			terminalRef.current = signature;
			try {
				await callbacks.current.refreshMeta();
			} catch (error) {
				terminalRef.current = "";
				throw error;
			}
			if (scopeRef.current !== key) return;
			setState(nextState);
			setRevision((value) => value + 1);
			if (target) callbacks.current.goToStep(target);
		},
		[key]
	);

	useEffect(() => {
		terminalRef.current = "";
		setState({ key, record: {} });
		setBusy(false);
	}, [key]);

	useEffect(() => {
		if (enabled && seed)
			void acceptResponse(seed).catch(() => {
				toast.error(
					"The wizard could not be refreshed. Reload to try again."
				);
			});
	}, [acceptResponse, enabled, seed]);

	const getStatus = useCallback(async () => {
		return kind === "lithe"
			? await services.litheService.getQuickStart(id)
			: await services.ledgerService.getQuickStart(id);
	}, [id, kind]);

	useEffect(() => {
		if (!isRunning || !id) return;
		let active = true;
		let pending = false;
		let checkQueued = false;
		let source: EventSource | null = null;
		let reconnect:
			| ReturnType<typeof setTimeout>
			| undefined;
		let heartbeat:
			| ReturnType<typeof setTimeout>
			| undefined;
		let attempts = 0;
		let progressVersion = 0;
		const channel = `${kind}:${id}`;
		const service = services.realtimeServiceInstance;
		const check = async () => {
			if (!active) return;
			if (pending) {
				checkQueued = true;
				return;
			}
			pending = true;
			const checkedVersion = progressVersion;
			try {
				const response = await getStatus();
				// A GET started before a progress event can contain an older running step.
				if (
					active &&
					(checkedVersion === progressVersion ||
						response.quick_start?.status !== "running")
				)
					await acceptResponse(response);
			} catch {
				// Keep the watchdog alive across transient HTTP/stream failures.
			} finally {
				pending = false;
				if (active && checkQueued) {
					checkQueued = false;
					void check();
				}
			}
		};
		const resetHeartbeat = () => {
			if (!active) return;
			clearTimeout(heartbeat);
			heartbeat = setTimeout(scheduleReconnect, 45_000);
		};
		const scheduleReconnect = () => {
			if (!active || reconnect) return;
			source?.close();
			clearTimeout(heartbeat);
			reconnect = setTimeout(
				() => {
					reconnect = undefined;
					void connect();
				},
				Math.min(1_000 * 2 ** attempts++, 10_000)
			);
		};
		const connect = async () => {
			try {
				const ticket = await service.createTicket([
					channel,
				]);
				if (!active) return;
				if (
					!ticket.token ||
					!ticket.channels.includes(channel)
				) {
					scheduleReconnect();
					return;
				}
				source = new EventSource(
					service.getStreamUrl(ticket.token)
				);
				source.addEventListener("control.connected", () => {
					attempts = 0;
					resetHeartbeat();
					void check();
				});
				source.addEventListener(
					`${kind}.quick_start.step_advanced`,
					(event) => {
						if (!active) return;
						try {
							const envelope: {
								type?: string;
								channel?: string;
								subject?: string;
								data?: TQuickStartResponse;
							} = JSON.parse(
								(event as MessageEvent<string>).data
							);
							if (
								envelope.type !==
									`${kind}.quick_start.step_advanced` ||
								envelope.channel !== channel ||
								envelope.subject !== "quick_start" ||
								typeof envelope.data?.current_step !==
									"string" ||
								!envelope.data.current_step ||
								envelope.data.quick_start?.status !==
									"running"
							)
								return;
							progressVersion++;
							void acceptResponse(envelope.data);
						} catch {
							/* The watchdog covers malformed events. */
						}
					}
				);
				source.addEventListener(
					`${kind}.quick_start.finished`,
					(event) => {
						try {
							const envelope = JSON.parse(
								(event as MessageEvent<string>).data
							);
							if (
								envelope.channel === channel &&
								envelope.subject === "quick_start"
							)
								void check();
						} catch {
							/* The watchdog covers malformed events. */
						}
					}
				);
				source.addEventListener(
					"control.heartbeat",
					resetHeartbeat
				);
				source.addEventListener(
					"control.subscribed",
					resetHeartbeat
				);
				source.onerror = scheduleReconnect;
				resetHeartbeat();
			} catch {
				scheduleReconnect();
			}
		};
		// This GET also restarts a stalled chain; never replace it with stream heartbeats.
		const watchdog = setInterval(() => {
			void check();
		}, 60_000);
		void check();
		void connect();
		return () => {
			active = false;
			source?.close();
			clearInterval(watchdog);
			clearTimeout(reconnect);
			clearTimeout(heartbeat);
		};
	}, [acceptResponse, getStatus, id, isRunning, kind]);

	const start = useCallback(
		async (skip = false) => {
			setBusy(true);
			try {
				const response =
					kind === "lithe"
						? await services.litheService.startQuickStart(
								id,
								skip
							)
						: await services.ledgerService.startQuickStart(
								id,
								skip
							);
				await acceptResponse(response, true);
			} catch (error) {
				toast.error(
					getApiErrorMessage(
						error,
						"Paul - Autonomous could not be started."
					)
				);
			} finally {
				if (scopeRef.current === key) setBusy(false);
			}
		},
		[acceptResponse, id, key, kind]
	);

	const reopen = useCallback(
		async (step: string) => {
			setBusy(true);
			try {
				const response =
					kind === "lithe"
						? await services.litheService.reopenStep(
								id,
								step
							)
						: await services.ledgerService.reopenStep(
								id,
								step
							);
				const snapshot = await getStatus();
				await acceptResponse(
					{
						...snapshot,
						current_step: response.current_step,
					},
					true
				);
			} catch (error) {
				toast.error(
					getApiErrorMessage(
						error,
						"This screen could not be reopened."
					)
				);
			} finally {
				if (scopeRef.current === key) setBusy(false);
			}
		},
		[acceptResponse, getStatus, id, key, kind]
	);

	return {
		record,
		currentStep,
		isRunning,
		busy,
		revision,
		acceptResponse,
		start,
		reopen,
	};
};
